import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useLazyGetInstallmentOptionsQuery, useProcessPaymentMutation, useLazyValidateCardBinQuery } from '../../../../api/endpoints/creditCardPayment';
import { useGetUserParamsQuery } from '../../../../api/endpoints/user';
import { toastService } from '../../../../utils/toastService';
import { useLanguage } from '../../../../contexts/LanguageContext';

const INITIAL_FORM_STATE = {
    // Tutar bilgileri
    amount: null,
    currency: null, // Sayfa yüklendiğinde userParams'tan dealerCurrency alınacak

    // Kart bilgileri
    cardNumber: '',
    cardHolderName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',

    // Taksit seçimi
    selectedInstallment: null
};

const useCreditCardPayment = () => {
    const { th } = useLanguage();

    // User params - dealerCurrency için
    const { data: userParams } = useGetUserParamsQuery();

    // Form state
    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(false);

    // userParams'tan dealerCurrency geldiğinde currency'yi güncelle
    useEffect(() => {
        if (userParams?.dealerCurrency && !formData.currency) {
            setFormData((prev) => ({
                ...prev,
                currency: userParams.dealerCurrency
            }));
        }
    }, [userParams?.dealerCurrency, formData.currency]);

    // Taksit seçenekleri state
    const [installmentOptions, setInstallmentOptions] = useState([]);
    const [showInstallments, setShowInstallments] = useState(false);

    // Taksit sorgusundan gelen POS bilgileri
    const [virtualPosId, setVirtualPosId] = useState(null);
    const [cardType, setCardType] = useState(null);
    const [bankName, setBankName] = useState(null);

    // Kart bilgisi state
    const [cardInfo, setCardInfo] = useState(null);

    // Debounce için ref
    const installmentFetchTimeoutRef = useRef(null);

    // API hooks
    const [getInstallments, { isLoading: isLoadingInstallments }] = useLazyGetInstallmentOptionsQuery();
    const [processPayment, { isLoading: isProcessingPayment }] = useProcessPaymentMutation();
    const [validateCardBin, { isLoading: isValidatingCard }] = useLazyValidateCardBinQuery();

    // Form alanını güncelle
    const updateField = useCallback((field, value) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value
        }));

        // Hata varsa temizle
        if (errors[field]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    }, [errors]);

    // Kart numarası değiştiğinde BIN kontrolü yap
    const handleCardNumberChange = useCallback(
        async (value) => {
            // Sadece rakamları al
            const cleanValue = value.replace(/\D/g, '');
            updateField('cardNumber', cleanValue);

            // İlk 6 hane girildiğinde kart tipini kontrol et
            if (cleanValue.length >= 6) {
                const bin = cleanValue.substring(0, 6);
                try {
                    const result = await validateCardBin(bin).unwrap();
                    setCardInfo(result);
                } catch (error) {
                    console.error('Card BIN validation error:', error);
                    setCardInfo(null);
                }
            } else {
                setCardInfo(null);
                // 6 haneden az ise taksit seçeneklerini temizle
                setInstallmentOptions([]);
                setShowInstallments(false);
            }
        },
        [updateField, validateCardBin]
    );

    // Otomatik taksit seçenekleri getirme - Kart numarası (min 6 hane) ve tutar girildiğinde
    useEffect(() => {
        // Önceki timeout'u temizle
        if (installmentFetchTimeoutRef.current) {
            clearTimeout(installmentFetchTimeoutRef.current);
        }

        // Koşulları kontrol et
        const hasValidAmount = formData.amount && formData.amount > 0;
        const hasValidCardBin = formData.cardNumber && formData.cardNumber.length >= 6;

        if (hasValidAmount && hasValidCardBin) {
            // Debounce ile API çağrısı yap (500ms bekle)
            installmentFetchTimeoutRef.current = setTimeout(async () => {
                try {
                    // cardBin: 8 hane varsa 8, yoksa 6 hane gönder (backend 6 veya 8 hane kabul ediyor)
                    const cardBinLength = formData.cardNumber.length >= 8 ? 8 : 6;
                    const result = await getInstallments({
                        amount: formData.amount,
                        currency: formData.currency,
                        cardBin: formData.cardNumber.substring(0, cardBinLength)
                    }).unwrap();

                    if (result.success && result.installments) {
                        setInstallmentOptions(result.installments);
                        setShowInstallments(true);

                        // Taksit sorgusundan gelen POS bilgilerini sakla
                        setVirtualPosId(result.virtualPosId);
                        setCardType(result.cardType);
                        setBankName(result.bankName);

                        // Varsayılan olarak tek çekim seç (eğer henüz seçim yapılmamışsa)
                        if (!formData.selectedInstallment) {
                            const singlePayment = result.installments.find((i) => i.installmentCount === 1);
                            if (singlePayment) {
                                setFormData((prev) => ({
                                    ...prev,
                                    selectedInstallment: singlePayment
                                }));
                            }
                        } else {
                            // Mevcut seçimi yeni tutarlarla güncelle
                            const updatedInstallment = result.installments.find(
                                (i) => i.installmentCount === formData.selectedInstallment.installmentCount
                            );
                            if (updatedInstallment) {
                                setFormData((prev) => ({
                                    ...prev,
                                    selectedInstallment: updatedInstallment
                                }));
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error fetching installments:', error);
                }
            }, 500);
        } else {
            // Koşullar sağlanmıyorsa taksit seçeneklerini temizle
            setInstallmentOptions([]);
            setShowInstallments(false);
        }

        // Cleanup
        return () => {
            if (installmentFetchTimeoutRef.current) {
                clearTimeout(installmentFetchTimeoutRef.current);
            }
        };
    }, [formData.amount, formData.currency, formData.cardNumber, getInstallments]);

    // Form validasyonu
    const validateForm = useCallback(() => {
        const newErrors = {};

        // Tutar kontrolü
        if (!formData.amount || formData.amount <= 0) {
            newErrors.amount = th('creditCardCollection.errors.amountRequired') || 'Tutar zorunludur';
        }

        // Para birimi kontrolü
        if (!formData.currency) {
            newErrors.currency = th('creditCardCollection.errors.currencyRequired') || 'Para birimi seçiniz';
        }

        // Kart numarası kontrolü (16 hane)
        if (!formData.cardNumber || formData.cardNumber.length !== 16) {
            newErrors.cardNumber = th('creditCardCollection.errors.cardNumberInvalid') || 'Geçerli bir kart numarası giriniz';
        }

        // Kart sahibi adı kontrolü
        if (!formData.cardHolderName || formData.cardHolderName.trim().length < 3) {
            newErrors.cardHolderName = th('creditCardCollection.errors.cardHolderNameRequired') || 'Kart sahibi adı zorunludur';
        }

        // Son kullanma tarihi kontrolü
        if (!formData.expiryMonth) {
            newErrors.expiryMonth = th('creditCardCollection.errors.expiryMonthRequired') || 'Ay seçiniz';
        }

        if (!formData.expiryYear) {
            newErrors.expiryYear = th('creditCardCollection.errors.expiryYearRequired') || 'Yıl seçiniz';
        }

        // CVV kontrolü (3 hane)
        if (!formData.cvv || formData.cvv.length !== 3) {
            newErrors.cvv = th('creditCardCollection.errors.cvvInvalid') || 'Geçerli bir CVV giriniz';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [formData, th]);

    // Taksit seçimi
    const selectInstallment = useCallback(
        (installment) => {
            updateField('selectedInstallment', installment);
        },
        [updateField]
    );

    // Formu sıfırla
    const resetForm = useCallback(() => {
        setFormData({
            ...INITIAL_FORM_STATE,
            currency: userParams?.dealerCurrency || null
        });
        setErrors({});
        setSubmitted(false);
        setInstallmentOptions([]);
        setShowInstallments(false);
        setCardInfo(null);
        setVirtualPosId(null);
        setCardType(null);
        setBankName(null);
    }, [userParams?.dealerCurrency]);

    // Transaction Status ID değerleri
    const TRANSACTION_STATUS = {
        SUCCESS: 1,
        FAIL: 2,
        ERROR: 3,
        WAITING_3D: 4,
        UNKNOWN: 5
    };

    // Ödemeyi tamamla
    const handlePayment = useCallback(async () => {
        setSubmitted(true);

        if (!validateForm()) {
            toastService.error(th('creditCardCollection.errors.formInvalid'));
            return { success: false };
        }

        if (!formData.selectedInstallment) {
            toastService.error(th('creditCardCollection.errors.selectInstallment'));
            return { success: false };
        }

        if (!virtualPosId) {
            toastService.error(th('creditCardCollection.errors.virtualPosIdMissing'));
            return { success: false };
        }

        try {
            // Backend API'nin beklediği request yapısı
            const paymentData = {
                amount: formData.selectedInstallment.totalAmount,
                originalAmount: formData.amount,
                currency: formData.currency,
                installment: formData.selectedInstallment.installmentCount,
                virtualPosId: virtualPosId,
                cardType: cardType,
                bankName: bankName,
                creditCard: {
                    cardHolderName: formData.cardHolderName.trim(),
                    cardNumber: formData.cardNumber,
                    expMonth: parseInt(formData.expiryMonth, 10),
                    expYear: parseInt(formData.expiryYear, 10),
                    cvcNumber: formData.cvv
                }
            };

            const result = await processPayment(paymentData).unwrap();

            // Response kontrolü
            if (result.success && result.isSuccess) {
                // TransactionStatusId'ye göre işlem
                switch (result.transactionStatusId) {
                    case TRANSACTION_STATUS.SUCCESS: {
                        // Direkt başarı - 3D'siz ödeme tamamlandı
                        toastService.success(th('creditCardCollection.success.paymentCompleted'));
                        
                        // Success sayfasına yönlendir
                        const successParams = new URLSearchParams({
                            transactionId: result.transactionId || '',
                            referenceCode: result.referenceCode || '',
                            bankName: bankName || '',
                            paymentType: result.paymentTypeName || '',
                            amount: String(formData.selectedInstallment?.totalAmount || formData.amount || ''),
                            currency: formData.currency || 'TRY',
                            installmentCount: String(formData.selectedInstallment?.installmentCount || 1)
                        });
                        
                        resetForm();
                        window.location.href = `/parts/credit-card-collection/success?${successParams.toString()}`;
                        return { success: true, data: result };
                    }

                    case TRANSACTION_STATUS.WAITING_3D:
                        // 3D Secure yönlendirmesi gerekiyor
                        if (result.threeDUrl) {
                            toastService.info(th('creditCardCollection.info.redirecting3D'));
                            // Bankaya yönlendir
                            window.location.href = result.threeDUrl;
                            return { success: true, redirectTo3D: true };
                        } else {
                            // Bu frontend hatası - backend'den threeDUrl gelmedi
                            toastService.error(th('creditCardCollection.errors.threeDUrlMissing'));
                            return { success: false };
                        }

                    case TRANSACTION_STATUS.FAIL:
                    case TRANSACTION_STATUS.ERROR:
                    case TRANSACTION_STATUS.UNKNOWN:
                    default:
                        // Backend hatası - interceptor'da gösteriliyor, burada tekrar gösterme
                        return { success: false };
                }
            } else {
                // Backend hatası - interceptor'da gösteriliyor, burada tekrar gösterme
                return { success: false };
            }
        } catch (error) {
            // Backend hatası - interceptor'da gösteriliyor, burada tekrar gösterme
            console.error('Payment error:', error);
            return { success: false };
        }
    }, [formData, processPayment, validateForm, th, virtualPosId, cardType, bankName, resetForm]);

    // Maskelenmiş kart numarası
    const maskedCardNumber = useMemo(() => {
        if (!formData.cardNumber) return '';
        const clean = formData.cardNumber.replace(/\D/g, '');
        const groups = clean.match(/.{1,4}/g) || [];
        return groups.join(' ');
    }, [formData.cardNumber]);

    // Ay seçenekleri
    const monthOptions = useMemo(
        () =>
            Array.from({ length: 12 }, (_, i) => ({
                label: String(i + 1).padStart(2, '0'),
                value: String(i + 1).padStart(2, '0')
            })),
        []
    );

    // Yıl seçenekleri (mevcut yıldan 2099'a kadar)
    const yearOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();
        const maxYear = 2099;
        const yearCount = maxYear - currentYear + 1;
        return Array.from({ length: yearCount }, (_, i) => ({
            label: String(currentYear + i),
            value: String(currentYear + i)
        }));
    }, []);

    return {
        // State
        formData,
        errors,
        submitted,
        installmentOptions,
        showInstallments,
        cardInfo,
        maskedCardNumber,
        monthOptions,
        yearOptions,
        bankName,

        // Loading states
        isLoadingInstallments,
        isProcessingPayment,
        isValidatingCard,
        isLoading: isLoadingInstallments || isProcessingPayment || isValidatingCard,

        // Actions
        updateField,
        handleCardNumberChange,
        selectInstallment,
        handlePayment,
        resetForm,
        validateForm
    };
};

export default useCreditCardPayment;
